// The Studio Editor's dress section — who is in the photo (one person / two / a group) and the dress for each — shared with
// Bulk Photo Edit. Controlled: `people` and `dresses` ({ single, left, right, men, women, children }) come in, and every change
// goes out through `onPeopleChange(people)` / `onDressChange(key, dress)`. `useStudioDressOptions` is the dress list and colours
// the account allows.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/authStore';
import { ALL_DRESS_COLORS } from '../../../constants/dressColors';
import { BD_ONLY_GARMENTS, CHILD_GARMENTS, FEMALE_GARMENTS, GARMENTS, parseDress } from './studioConstants';
import { GroupDressTabs, PeopleSelector } from './StudioControls';
import StudioDressPicker from './StudioDressPicker';

/** Every dress style we have (the AI editor's Settings → dress list does NOT narrow the studio), minus the Bangladesh-only ones
 *  for users from another country; the colours the account enabled; whether suits show their shirt / tie colour options. */
export function useStudioDressOptions() {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const userCC = String(user?.country || '').toUpperCase();
    const isBdUser = !userCC || userCC === 'BD';
    const enabledDressColors = user?.settings?.enabledDressColors;
    const showSubColors = user?.settings?.showSuitColorOptions === true;
    const garments = useMemo(() => GARMENTS.filter((g) => isBdUser || !BD_ONLY_GARMENTS.includes(g.value)), [isBdUser]);
    const colors = useMemo(
        () => ALL_DRESS_COLORS
            .filter((c) => !enabledDressColors?.length || enabledDressColors.includes(c.id))
            .map((c) => ({ id: c.id, label: t(c.i18nKey) || c.colorLabel, hex: c.hex })),
        [enabledDressColors, t],
    );
    return { garments, colors, showSubColors };
}

const isChanged = (d) => parseDress(d?.value).type !== 'original';

export default function StudioDressSection({ people, dresses, onPeopleChange, onDressChange, disabled = false }) {
    const { t } = useTranslation();
    const { garments, colors, showSubColors } = useStudioDressOptions();
    const [groupTab, setGroupTab] = useState('men');
    const picker = (key, list = garments) => (
        <StudioDressPicker
            key={key}
            dress={dresses[key]}
            onChange={(d) => onDressChange(key, d)}
            garments={list}
            colors={colors}
            showSubColors={showSubColors}
            disabled={disabled}
            personKey={key}
        />
    );
    const label = (text) => <p className="mb-1.5 text-xs !font-semibold text-zinc-500 dark:text-zinc-400">{text}</p>;
    return (
        <div className="space-y-4">
            <div>
                {label(t('studioEditor.peopleTitle'))}
                <PeopleSelector value={people} onChange={onPeopleChange} disabled={disabled} />
            </div>
            <div className="border-t border-zinc-100 pt-4 dark:border-zinc-800">
                {people === 'group' ? (
                    <div className="space-y-3">
                        <GroupDressTabs
                            value={groupTab}
                            onChange={setGroupTab}
                            chosen={{ men: isChanged(dresses.men), women: isChanged(dresses.women), children: isChanged(dresses.children) }}
                            disabled={disabled}
                        />
                        {groupTab === 'men' && picker('men', garments.filter((g) => !FEMALE_GARMENTS.includes(g.value)))}
                        {groupTab === 'women' && picker('women', garments.filter((g) => FEMALE_GARMENTS.includes(g.value)))}
                        {groupTab === 'children' && picker('children', CHILD_GARMENTS)}
                    </div>
                ) : people === 'dual' ? (
                    <div className="space-y-4">
                        <div>{label(t('imageGeneration.leftPerson'))}{picker('left')}</div>
                        <div>{label(t('imageGeneration.rightPerson'))}{picker('right')}</div>
                    </div>
                ) : picker('single')}
            </div>
        </div>
    );
}
